/**
 * One vocabulary for refusals and one precedence (mission 6 T012; FR-016,
 * FR-026, C-006; research §1.9; mission 5 FR-020; post-plan C12; post-analysis
 * I3, U1).
 *
 * `PROBLEM` mirrors the type constants of backend/internal/httpapi/problem.go
 * one for one, on the `https://trellis.bloomcredit.io/problems/<type>` scheme;
 * `problems.test.ts` reads problem.go, so a type added there fails until it is
 * added here. `PROBLEM_MEMBERS` are the extension members, typed on `Problem`.
 *
 * `GATE_RENDERING` is keyed `<type>|<status>|<requirement>`: the two 410
 * `not-found` rows differ only by requirement (D.2 retention, U.1 an expired
 * grant). `resolveProblem` returns data only; WP03's `ProblemBand` renders it,
 * with the requirement as `data-requirement` (a key, not visible text).
 *
 * Precedence: the calling screen's override (contracts/ui-api.md), then the
 * table, then `failureCopy()`. Rendering never keys on `detail`; `try-again`
 * renders its `detail` verbatim, and the B.3 and P.4 rows show it beneath the
 * remedy.
 *
 * Mission 8 WP07 T035 (OR-11, U2, amendment 15; research §1.3): `Band` gains
 * `warning` (a busy or unavailable assistant: the conversation stays readable),
 * and five assistant rows. The throttle's remedy is a render entry, so
 * `remedyText` formats `ceiling` and `date(resets_at)` for it; the 429's `used`
 * is the settled count (C5), so the notice appears only at the ceiling.
 *
 * Mission 9 WP11 T067 (FR-008's SPA half; research §1.18; contracts/ui-api.md "Spine"; U21, U25):
 * four billing rows. `byMember` chooses a row's body by the problem's `reason` and its remedy link by
 * its `remedy`; `body` is a row's fixed body. A billing problem renders register copy only, never
 * `detail`: the remedy link leads (ProblemBand's linked line) and the body sits in the line beneath
 * (`ResolvedProblem.detail`); with no remedy link the body leads, and with neither the band names
 * no remedy, so ProblemBand shows the problem's title, as for X.10 and TX.4. The three 402s
 * (`insufficient-credits`, `billing-read-only`, `payment-failed`) are told apart by type.
 *
 * Mission 10 WP15 T085 (FR-005, FR-010, FR-013, FR-017; research §1.22, §1.27): three rows, register 37
 * copy only, never `detail`. `retention-confirmation-required` (D.2) carries `dialog: 'shorten'`: the screen
 * opens the shortening dialog from the problem's `due_count`, `runs` and `truncated`
 * (`queries.ts` `retentionConfirmation`). `reauthentication-failed` (D.6) is a 403, so the session handler,
 * which acts on the 401 `unauthorized` only, never signs the reporter out for a mistyped password.
 * `tenant-key-missing` (D.3) is the upload grant's 503 while the tenant's storage key is not ready.
 */
import { InvalidIdError, idSegment, type Problem, type ProblemMembers } from './api'
import { date } from './format'
import { PROVISIONAL_COPY, copyText, type TextKey } from './provisional-copy'

const BASE = 'https://trellis.bloomcredit.io/problems/'

/** Problem types, mirrored from problem.go (session-and-gate.md, and missions 2 to 8). */
export const PROBLEM = {
  UNAUTHORIZED: `${BASE}unauthorized`,
  NOT_FOUND: `${BASE}not-found`,
  INVALID_CREDENTIALS: `${BASE}invalid-credentials`,
  MFA_REQUIRED: `${BASE}mfa-required`,
  VERIFICATION_REQUIRED: `${BASE}verification-required`,
  ORIGIN_NOT_ALLOWED: `${BASE}origin-not-allowed`,
  VALIDATION_FAILED: `${BASE}validation-failed`,
  INVALID_CODE: `${BASE}invalid-code`,
  INVALID_OR_EXPIRED_TOKEN: `${BASE}invalid-or-expired-token`,
  ACCOUNT_EXISTS: `${BASE}account-exists`,
  ALREADY_ENROLLED: `${BASE}already-enrolled`,
  TRY_AGAIN: `${BASE}try-again`,
  /** 500: a failure the contract has no answer for; the body carries nothing else (NFR-004). */
  INTERNAL: `${BASE}internal`,
  /** 501: a skeleton stub; not a state the SPA branches on. */
  NOT_IMPLEMENTED: `${BASE}not-implemented`,
  // Mission 2, uploads.
  REQUEST_TOO_LARGE: `${BASE}request-too-large`,
  UPLOAD_TOO_LARGE: `${BASE}upload-too-large`,
  UPLOAD_UNSUPPORTED_TYPE: `${BASE}upload-unsupported-type`,
  LAYOUT_VERSION_UNKNOWN: `${BASE}layout-version-unknown`,
  UPLOAD_OBJECT_MISMATCH: `${BASE}upload-object-mismatch`,
  UPLOAD_REJECTED_BY_SCAN: `${BASE}upload-rejected-by-scan`,
  // Mission 3, credits and runs.
  UPLOAD_OBJECT_CHANGED: `${BASE}upload-object-changed`,
  INSUFFICIENT_CREDITS: `${BASE}insufficient-credits`,
  UPLOAD_NOT_STARTABLE: `${BASE}upload-not-startable`,
  RUN_NOT_CANCELLABLE: `${BASE}run-not-cancellable`,
  // Missions 4 and 5.
  FORBIDDEN: `${BASE}forbidden`,
  TOO_MANY_RECORDS: `${BASE}too-many-records`,
  RUN_NOT_READY: `${BASE}run-not-ready`,
  RUN_NOT_TRANSMISSIBLE: `${BASE}run-not-transmissible`,
  ERROR_ACKNOWLEDGMENT_REQUIRED: `${BASE}error-acknowledgment-required`,
  // Mission 7, credentials and transmit (credentials-api "Problem types added by mission 7").
  PROCESSING_TERMS_REQUIRED: `${BASE}processing-terms-required`,
  BUREAU_NOT_VERIFIED: `${BASE}bureau-not-verified`,
  BUREAU_DISABLED: `${BASE}bureau-disabled`,
  CREDENTIAL_NOT_ENTERED: `${BASE}credential-not-entered`,
  /** TX.6 (post-plan C12), not TX.5. */
  TRANSMISSION_IN_PROGRESS: `${BASE}transmission-in-progress`,
  HOST_NOT_ALLOWED: `${BASE}host-not-allowed`,
  /** 429, SEC.6: the Test connection rate limit. */
  TOO_MANY_REQUESTS: `${BASE}too-many-requests`,
  // Mission 8, the assistant (assistant-api "Refusals"; research §1.3; FR-004, FR-005).
  /** 429, SAT.5: the cycle's settled messages are at the fair-use ceiling. */
  FAIR_USE_CEILING_REACHED: `${BASE}fair-use-ceiling-reached`,
  /** 409: reason run-data-gone (D.2) or no-credit-cycle (SAT.5). */
  ASSISTANT_UNAVAILABLE: `${BASE}assistant-unavailable`,
  // Mission 9, billing (billing-api "Problem types added"; research §1.18; FR-008). WP01 adds the
  // types and members so the parity case stays green (TR-5, E1); WP11 adds the rendering rows.
  /** 402, B.1: read-only or pending (amendment 7); members reason, remedy, read_only_since. */
  BILLING_READ_ONLY: `${BASE}billing-read-only`,
  /** 409, B.1 (B.4 on the purchase): members reason and remedy. */
  BILLING_STATE_CONFLICT: `${BASE}billing-state-conflict`,
  /** 402, B.4: the card was declined. */
  PAYMENT_FAILED: `${BASE}payment-failed`,
  /** 400, B.1: the webhook's signature refusal; never reaches the SPA. */
  WEBHOOK_SIGNATURE_INVALID: `${BASE}webhook-signature-invalid`,
  // Mission 10, retention and the tenant lifecycle (research §1.22; FR-005, FR-010, FR-013). WP01 adds the
  // types and members so the parity case stays green on every backend lane; WP15 adds the rendering rows.
  /** 409, D.2: the new window makes runs due at once; members due_count, runs, truncated. */
  RETENTION_CONFIRMATION_REQUIRED: `${BASE}retention-confirmation-required`,
  /** 403, D.6: the deletion request's password was refused; not a sign-out. */
  REAUTHENTICATION_FAILED: `${BASE}reauthentication-failed`,
  /** 503, D.3: the tenant has no storage key yet. */
  TENANT_KEY_MISSING: `${BASE}tenant-key-missing`,
} as const

export type ProblemType = (typeof PROBLEM)[keyof typeof PROBLEM]

/** The extension members problem.go emits, besides `detail`, `instance` and `requirement`. */
export const PROBLEM_MEMBERS = [
  'errors',
  'accepted',
  'state',
  'run_id',
  'required',
  'balance_before',
  'shortfall',
  'first_cycle',
  'forgiveness_remaining',
  'reason',
  'record_count',
  'admission_cap',
  'record_count_is_minimum',
  'bureau',
  'error_count',
  'ack_wording_version',
  'retry_after_seconds',
  'used',
  'ceiling',
  'resets_at',
  'remedy',
  'read_only_since',
  'due_count',
  'runs',
  'truncated',
] as const satisfies ReadonlyArray<keyof ProblemMembers>

export type Band = 'blocked' | 'warning' | 'info' | 'reserved'

/**
 * A remedy is a register sentence; the render entries take the problem's members. `overCapPathB` is
 * `overCap` in base segments (owner decision 2026-09-26). `testRateLimited` takes `retry_after_seconds`
 * in minutes (mission 7 WP07 T041; SEC.6). `assistantThrottled` takes `ceiling` and `resets_at` (mission 8
 * WP07 T035; SAT.5).
 */
export type RemedyKey = TextKey | 'overCap' | 'overCapPathB' | 'runNotReady' | 'testRateLimited' | 'assistantThrottled'

/** A remedy link a problem member names (U21): the register sentence and where it goes. */
export interface MemberRemedy {
  key: TextKey
  to: string
}

export interface GateRendering {
  band: Band
  /** The band interrupts: `role="alert"` (B.3). */
  alert?: true
  /** The register entry naming the remedy, or null when the band names none. */
  remedy: RemedyKey | null
  /** Where the remedy links. */
  to?: string
  /** The problem's `detail` is shown verbatim beneath the remedy (B.3, P.4). */
  detail?: true
  /** Link the run in `run_id` when it is a run id; otherwise use this remedy instead. */
  runLink?: { otherwise: RemedyKey }
  /** Mission 9 (U21): the body by the problem's `reason`, and the remedy link by its `remedy`. */
  byMember?: { reason?: Record<string, TextKey>; remedy?: Record<string, MemberRemedy> }
  /** Mission 9: a fixed register body, beneath the remedy (payment-failed). */
  body?: TextKey
  /** Mission 10: the screen opens this dialog, rendered from the problem's members (D.2). */
  dialog?: 'shorten'
}

/** What ProblemBand renders. */
export interface ResolvedProblem {
  band: Band
  alert: boolean
  /** The problem's requirement, for `data-requirement`. */
  requirement: string | null
  remedy: string | null
  to?: string
  detail: string | null
  /** Mission 10: the dialog the screen opens for this problem. */
  dialog?: 'shorten'
}

/** A screen's own renderings, keyed like GATE_RENDERING or by the bare type slug. */
export type ProblemOverrides = Partial<Record<string, GateRendering>>

// Mission 9 (U21): the billing remedy links and the state-conflict bodies, shared by the rows below.
const CARD = '/app/settings/billing#card'
const UPDATE_PAYMENT: MemberRemedy = { key: 'readOnlyRemedyUpdate', to: CARD }
const SUBSCRIBE: MemberRemedy = { key: 'readOnlyRemedySubscribe', to: '/app/subscribe' }
const STATE_REASONS: Record<string, TextKey> = {
  active: 'problemState.active',
  cancelling: 'problemState.cancelling',
  dunning: 'problemState.dunning',
  'no-subscription': 'problemState.no-subscription',
  'not-cancelling': 'problemState.not-cancelling',
  'access-ended': 'problemState.access-ended',
  'same-plan': 'problemState.same-plan',
  'request-reused': 'problemState.request-reused',
  ending: 'problemState.ending',
}

/** Mission 5 FR-020's gate table, as the SPA renders it (contracts/ui-api.md). */
export const GATE_RENDERING: Record<string, GateRendering> = {
  'insufficient-credits|402|B.3': {
    band: 'blocked',
    alert: true,
    remedy: 'buyCreditsRemedy',
    to: '/app/settings/billing',
    detail: true,
  },
  'too-many-records|422|P.4': { band: 'blocked', remedy: 'overCap', detail: true },
  'run-not-ready|409|GEN.4': { band: 'info', remedy: 'runNotReady' },
  'not-found|410|D.2': { band: 'blocked', remedy: 'removedUnderRetention' },
  'not-found|410|U.1': { band: 'blocked', remedy: 'uploadExpired' },
  'run-not-transmissible|409|X.10': { band: 'blocked', remedy: null },
  // Mission 7 WP07 T041 (contracts/ui-api.md "GATE_RENDERING rows"; FR-008): TX.4 is returned now, and
  // blocks with the count-changed remedy; the screens override where the contract says so.
  'error-acknowledgment-required|409|TX.4': { band: 'blocked', remedy: 'ackCountChanged' },
  'run-not-transmissible|409|GEN.6': { band: 'blocked', remedy: 'notDeliverableRemedy', to: '/app/settings/header' },
  'processing-terms-required|409|TX.3': { band: 'blocked', remedy: 'termsBlockedRemedy' },
  'bureau-disabled|409|TX.9': { band: 'blocked', remedy: 'killSwitchExplanation' },
  'bureau-not-verified|409|TX.2': { band: 'blocked', remedy: 'testConnectionFirst', to: '/app/settings/bureaus' },
  'credential-not-entered|409|TX.2': { band: 'blocked', remedy: 'enterCredentialsFirst', to: '/app/settings/bureaus' },
  'host-not-allowed|409|SEC.1': { band: 'blocked', remedy: 'hostNotAllowedRemedy', to: '/app/settings/bureaus' },
  'too-many-requests|429|SEC.6': { band: 'info', remedy: 'testRateLimited' },
  /** TX.6 (post-plan C12), not TX.5. */
  'transmission-in-progress|409|TX.6': { band: 'info', remedy: 'transmissionInProgress' },
  'origin-not-allowed|403|SEC.7': { band: 'blocked', remedy: 'reloadAndRetry' },
  'upload-object-changed|409|SEC.8': { band: 'blocked', remedy: 'uploadAgain' },
  'upload-object-mismatch|409|SEC.8': { band: 'blocked', remedy: 'uploadAgain' },
  'upload-not-startable|409|': { band: 'blocked', remedy: 'alreadyUsed', runLink: { otherwise: 'uploadAgain' } },
  // Mission 8 WP07 T035 (research §1.3; OR-11, U2; amendment 15: a mock throttle below the ceiling is busy).
  'fair-use-ceiling-reached|429|SAT.5': { band: 'blocked', remedy: 'assistantThrottled' },
  'try-again|503|SAT.5': { band: 'warning', remedy: 'assistantBusy' },
  'try-again|503|AV.8': { band: 'warning', remedy: 'assistantUnavailable' },
  'assistant-unavailable|409|D.2': { band: 'info', remedy: 'assistantRunDataGone' },
  'assistant-unavailable|409|SAT.5': { band: 'blocked', remedy: 'assistantNoCycle' },
  // Mission 9 WP11 T067 (research §1.18; contracts/ui-api.md "Spine"; U21, U25): register copy only, never detail.
  'billing-read-only|402|B.1': {
    band: 'blocked',
    remedy: null,
    byMember: {
      reason: { dunning: 'problemReadOnlyDunning', cancelled: 'problemReadOnlyCancelled', 'no-subscription': 'problemReadOnlyPending' },
      remedy: { 'update-payment': UPDATE_PAYMENT, subscribe: SUBSCRIBE },
    },
  },
  'billing-state-conflict|409|B.1': {
    band: 'warning',
    remedy: null,
    byMember: {
      reason: STATE_REASONS,
      remedy: { 'undo-cancel': { key: 'undoCancelRemedy', to: '/app/settings/billing' }, 'update-payment': UPDATE_PAYMENT, subscribe: SUBSCRIBE },
    },
  },
  // The purchase (B.4): its reasons are no-subscription and request-reused; only Subscribe is a remedy.
  'billing-state-conflict|409|B.4': { band: 'warning', remedy: null, byMember: { reason: STATE_REASONS, remedy: { subscribe: SUBSCRIBE } } },
  'payment-failed|402|B.4': { band: 'blocked', remedy: 'cardUpdate', to: CARD, body: 'problemPaymentFailed' },
  // Mission 10 WP15 T085 (research §1.22): register 37 copy only, never detail.
  'retention-confirmation-required|409|D.2': { band: 'warning', remedy: 'shortenTitle', dialog: 'shorten' },
  'reauthentication-failed|403|D.6': { band: 'blocked', remedy: 'deletionPasswordFailed' },
  'tenant-key-missing|503|D.3': { band: 'warning', remedy: 'problemTenantKeyMissing' },
}

function slug(problem: Problem): string {
  return problem.type.startsWith(BASE) ? problem.type.slice(BASE.length) : problem.type
}

/** `<type slug>|<status>|<requirement>`, the GATE_RENDERING key of a problem. */
export function gateKey(problem: Problem): string {
  return `${slug(problem)}|${problem.status}|${problem.requirement ?? ''}`
}

function remedyText(key: RemedyKey, problem: Problem): string {
  if (key === 'runNotReady') {
    return PROVISIONAL_COPY.runNotReady.render(problem.state === undefined ? {} : { state: problem.state })
  }
  if (key === 'testRateLimited') {
    const seconds = problem.retry_after_seconds
    return PROVISIONAL_COPY.testRateLimited.render(
      seconds === undefined ? {} : { minutes: Math.max(1, Math.ceil(seconds / 60)) },
    )
  }
  if (key === 'assistantThrottled') {
    const { ceiling, resets_at: resetsAt } = problem
    if (ceiling === undefined || resetsAt === undefined) return copyText('genericError')
    return PROVISIONAL_COPY.assistantThrottled.render({ ceiling, resetsAt: date(resetsAt) })
  }
  if (key === 'overCap' || key === 'overCapPathB') {
    const { record_count: recordCount, admission_cap: admissionCap } = problem
    if (recordCount === undefined || admissionCap === undefined) return copyText('genericError')
    return PROVISIONAL_COPY.overCap.render({
      recordCount,
      admissionCap,
      isMinimum: problem.record_count_is_minimum === true,
      ...(key === 'overCapPathB' ? { unit: 'base segments' as const } : {}),
    })
  }
  return copyText(key)
}

function runPath(runId: string | undefined): string | undefined {
  if (runId === undefined) return undefined
  try {
    return `/app/runs/${idSegment(runId, 'run id')}`
  } catch (err) {
    if (err instanceof InvalidIdError) return undefined
    throw err
  }
}

/** The register 16 fallback for a problem nothing else names. */
export function failureCopy(problem: Problem): ResolvedProblem {
  const busy = slug(problem) === 'try-again' && problem.detail ? problem.detail : null
  return {
    band: 'blocked',
    alert: false,
    requirement: problem.requirement ?? null,
    remedy: busy ?? copyText('genericError'),
    detail: null,
  }
}

/** The override, then the table, then failureCopy(). */
export function resolveProblem(problem: Problem, override?: ProblemOverrides): ResolvedProblem {
  const key = gateKey(problem)
  const rendering = override?.[key] ?? override?.[slug(problem)] ?? GATE_RENDERING[key]
  if (!rendering) return failureCopy(problem)

  let remedy = rendering.remedy
  let to = rendering.to
  if (rendering.runLink) {
    to = runPath(problem.run_id)
    if (to === undefined) remedy = rendering.runLink.otherwise
  }
  let detail = rendering.detail && problem.detail ? problem.detail : null
  if (rendering.byMember !== undefined || rendering.body !== undefined) {
    // Mission 9 (U21, U25): the body by reason and the link by remedy, register copy only.
    const body = rendering.byMember?.reason?.[problem.reason ?? ''] ?? rendering.body
    const link = rendering.byMember?.remedy?.[problem.remedy ?? '']
    if (link !== undefined) {
      remedy = link.key
      to = link.to
    }
    if (remedy === null) remedy = body ?? null
    else detail = body === undefined ? null : copyText(body)
  }
  const resolved: ResolvedProblem = {
    band: rendering.band,
    alert: rendering.alert === true,
    requirement: problem.requirement ?? null,
    remedy: remedy === null ? null : remedyText(remedy, problem),
    detail,
  }
  if (to !== undefined) resolved.to = to
  if (rendering.dialog !== undefined) resolved.dialog = rendering.dialog
  return resolved
}
